import {use, useCallback, useId, useRef} from 'react'
import type {RefObject} from 'react'
import {Banner, Link, Stack} from '@primer/react'
import type {StackProps} from '@primer/react'
import {InternalBannerStackDataContext} from './internal-banner-stack-data-context'
import {InternalBannerRegistryContext} from './internal-banner-registry-context'
import {useDismissBanner, useFocusBanner} from './hooks'

export interface BannerStackOutletProps extends StackProps<'div'> {
  /** Element that receives focus when a user dismisses the only banner in the stack. */
  focusFallbackRef: RefObject<HTMLElement | null>
}

export function BannerStackOutlet({focusFallbackRef, ...props}: BannerStackOutletProps) {
  const {banners} = use(InternalBannerStackDataContext)
  const {registerBannerEl} = use(InternalBannerRegistryContext)
  const dismissBanner = useDismissBanner()
  const focusBanner = useFocusBanner()
  const endId = useId()
  const endRef = useRef<HTMLDivElement>(null)

  const handleDismiss = useCallback(
    (id: string) => {
      const index = banners.findIndex(banner => banner.id === id)
      const focusTarget = banners[index - 1] ?? banners[index + 1]

      dismissBanner(id)

      if (focusTarget) {
        focusBanner(focusTarget.id)
      } else {
        focusFallbackRef?.current?.focus()
      }
    },
    [banners, dismissBanner, focusBanner, focusFallbackRef],
  )

  if (banners.length === 0) {
    return null
  }

  return (
    <Stack gap="condensed" direction="vertical" {...props}>
      {banners.length > 1 && (
        <Link
          href={`#${endId}`}
          className={'show-on-focus color-bg-accent-emphasis color-fg-on-emphasis p-3'}
          onClick={e => {
            e.preventDefault()
            // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-in-input-handler
            endRef.current?.focus()
          }}
        >
          Skip {banners.length} banners
        </Link>
      )}
      {banners.map(({dismissible, persistOnNavigation: _persistOnNavigation, onDismiss, ...banner}) => (
        <Banner
          key={banner.id}
          ref={el => registerBannerEl(banner.id, el)}
          {...banner}
          onDismiss={
            dismissible
              ? () => {
                  handleDismiss(banner.id)
                  onDismiss?.()
                }
              : undefined
          }
        />
      ))}
      {banners.length > 1 && <div id={endId} ref={endRef} tabIndex={-1} />}
    </Stack>
  )
}

BannerStackOutlet.displayName = 'BannerStackOutlet'
